import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface EnvironmentRow {
  readonly environmentId: string;
  readonly environmentCode: string;
  readonly environmentName: string;
  readonly environmentType: string;
  readonly status: string;
  readonly isProduction: boolean;
  readonly lastDeployedAt: string | null;
}

const TYPES = ['', 'DEVELOPMENT', 'QA', 'UAT', 'STAGING', 'PRE_PRODUCTION', 'PRODUCTION', 'DISASTER_RECOVERY', 'SANDBOX'];
const STATUSES = ['', 'AVAILABLE', 'UNAVAILABLE', 'MAINTENANCE', 'DECOMMISSIONED'];
const CREATE_TYPES = TYPES.filter(Boolean);
const SET_STATUSES = ['AVAILABLE', 'UNAVAILABLE', 'MAINTENANCE', 'DECOMMISSIONED'];

/**
 * M32 — Environment Dashboard (§31 / §21). Register of managed deployment
 * environments (T428) with type, availability status, production flag and the
 * currently deployed release. Supports registering an environment and
 * transitioning its availability status, each wired to the guarded
 * /v1/devops/environments write API. Filterable by environment type and status;
 * the availability status gates deployment authorisation
 * (M32_ENVIRONMENT_UNAVAILABLE).
 */
export function EnvironmentDashboardPage(): ReactNode {
  const { t: tr } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<EnvironmentRow[]>([]);
  const [environmentType, setEnvironmentType] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState('DEVELOPMENT');
  const [setStatusValue, setSetStatusValue] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (environmentType) params.set('environmentType', environmentType);
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: EnvironmentRow[] }>(`/v1/devops/environments${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, environmentType, status, reload]);

  const failMessage = (err: unknown): string =>
    err instanceof ApiError && err.status === 403
      ? tr('m32.environment.authError')
      : (err as Error).message;

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!newName.trim()) { setActionError(tr('m32.environment.nameRequired')); return; }
    setBusy(true);
    try {
      await apiRequest(`/v1/devops/environments`, {
        method: 'POST',
        body: { environmentName: newName.trim(), environmentType: newType },
        token,
      });
      setNotice(tr('m32.environment.registeredNotice', { name: newName.trim() }));
      setNewName('');
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const setStatusFor = async (r: EnvironmentRow): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/devops/environments/${r.environmentId}/status`, {
        method: 'POST',
        body: { status: setStatusValue[r.environmentId] ?? 'AVAILABLE' },
        token,
      });
      setNotice(tr('m32.environment.statusUpdated', { code: r.environmentCode }));
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err));
    }
  };

  return (
    <main aria-label="environment-dashboard">
      <h1>{tr('m32.environment.title')}</h1>

      <section aria-label="create-environment">
        <h2>{tr('m32.environment.registerTitle')}</h2>
        <form aria-label="environment-form" onSubmit={(e) => void create(e)}>
          <label>{tr('m32.environment.fieldName')}<input aria-label="field-environment-name" value={newName} onChange={(e) => setNewName(e.target.value)} /></label>
          <label>{tr('m32.environment.type')}
            <select aria-label="field-environment-type" value={newType} onChange={(e) => setNewType(e.target.value)}>
              {CREATE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <button type="submit" aria-label="submit-environment" disabled={busy}>{busy ? tr('m32.environment.registering') : tr('m32.environment.registerBtn')}</button>
        </form>
        {actionError && <p role="alert" aria-label="environment-error">{actionError}</p>}
        {notice && <p aria-label="environment-notice">{notice}</p>}
      </section>

      <div className="filters">
        <label>
          {tr('m32.environment.type')}
          <select aria-label="filter-environment-type" value={environmentType} onChange={(e) => setEnvironmentType(e.target.value)}>
            {TYPES.map((t) => <option key={t || 'all'} value={t}>{t || tr('m32.environment.all')}</option>)}
          </select>
        </label>
        <label>
          {tr('m32.environment.status')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || tr('m32.environment.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{tr('m32.environment.loading')}</p> : (
        <>
          <p aria-label="environment-count">{tr('m32.environment.count', { count: rows.length })}</p>
          <table aria-label="environment-table">
            <thead><tr><th>{tr('m32.environment.thCode')}</th><th>{tr('m32.environment.fieldName')}</th><th>{tr('m32.environment.type')}</th><th>{tr('m32.environment.thIsProduction')}</th><th>{tr('m32.environment.status')}</th><th>{tr('m32.environment.thActions')}</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.environmentId} data-status={r.status}>
                  <td>{r.environmentCode}</td><td>{r.environmentName}</td><td>{r.environmentType}</td>
                  <td>{r.isProduction ? 'YES' : 'no'}</td><td>{r.status}</td>
                  <td>
                    <select aria-label={`status-select-${r.environmentCode}`} value={setStatusValue[r.environmentId] ?? 'AVAILABLE'} onChange={(e) => setSetStatusValue((m) => ({ ...m, [r.environmentId]: e.target.value }))}>
                      {SET_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <button type="button" aria-label={`set-status-${r.environmentCode}`} onClick={() => void setStatusFor(r)}>{tr('m32.environment.setStatus')}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
